import { CONVERSATION_PRIORITIES, isConversationPriority, type ConversationPriority } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Select, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { useFormatters } from "../shared/format";
import { useAgentName } from "../shared/use-engage-data";
import { AssignMenu } from "./assign-menu";
import { ConversationAvatar } from "./conversation-avatar";
import { ConversationStatusBadge } from "./conversation-badges";
import { ConvertTicketDialog } from "./convert-ticket-dialog";
import type { useConversationActions } from "./inbox-api";
import { CHANNEL_ICON, EDITABLE_STATUSES, isEditableStatus, type ConversationDetail } from "./inbox-model";
import { LanguageChip } from "./language-chip";
import { PriorityChip, SlaChip } from "./priority-sla-chips";
import { SnoozeChip } from "./snooze-chip";
import { ConvertButton, ThreadTicketChips, useHasTicket } from "./ticket-chips";
import { UnsnoozeButton } from "./unsnooze-button";
import { RunWorkflowMenu } from "./run-workflow-menu";
import { useNow } from "./use-now";
import { useTicketSupport } from "./use-conversation-tickets";
import type { InboxPermissions } from "./use-inbox-permissions";

interface ThreadHeaderProps {
  detail: ConversationDetail;
  actions: ReturnType<typeof useConversationActions>;
  permissions: InboxPermissions;
  onBack: () => void;
  onToggleDetails: () => void;
  onTakeoverToggle: () => void;
  /** Awaiting staff: hand the chat back to the assistant without replying first. */
  onHandBack: () => void;
}

/** Thread title row: visitor, status, channel · agent · start, status/assign/takeover controls. */
export function ThreadHeader({ detail, actions, permissions, onBack, onToggleDetails, onTakeoverToggle, onHandBack }: ThreadHeaderProps) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const agentName = useAgentName();
  const errorMessage = useErrorMessage();
  const { conversation } = detail;
  const startedAt = detail.messages[0]?.created_at ?? conversation.last_message_at;
  const status = isEditableStatus(conversation.status) ? conversation.status : "closed";
  const busy = actions.takeover.isPending || actions.release.isPending;
  const now = useNow();
  const ticketSupport = useTicketSupport();
  const hasTicket = useHasTicket(conversation.id);
  const [converting, setConverting] = useState(false);

  const onStatus = (value: string) => {
    if (!isEditableStatus(value)) return;
    actions.setStatus.mutateAsync(value).then(
      () => toast.success(t("engage.inbox.status.changed", { status: t(`engage.inbox.status.${value}`) })),
      (error: unknown) => toast.error(errorMessage(error)),
    );
  };

  const onPriority = (priority: ConversationPriority) => {
    actions.setPriority.mutateAsync(priority).then(
      () => toast.success(t("engage.inbox.priority.changed", { priority: t(`engage.inbox.priority.${priority}`) })),
      (error: unknown) => toast.error(errorMessage(error)),
    );
  };

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-line bg-surface px-4 py-3 lg:px-6">
      <Button variant="ghost" size="sm" iconOnly className="lg:hidden" aria-label={t("engage.inbox.thread.back")} onClick={onBack}>
        <Icon name="arrow-left" />
      </Button>
      <ConversationAvatar name={conversation.visitor_name} />
      <div className="min-w-0 flex-1 basis-60">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h2 id="thread-heading" className="truncate text-base font-bold" data-testid="thread-title">{conversation.visitor_name}</h2>
          <ConversationStatusBadge item={conversation} />
          <PriorityChip priority={conversation.priority} />
          <LanguageChip code={conversation.detected_language} />
          <SlaChip target={conversation.sla_target} now={now} />
          <SnoozeChip item={conversation} now={now} />
          <ThreadTicketChips conversationId={conversation.id} />
        </div>
        <div className="flex flex-wrap items-center gap-x-2 text-xs text-fg-subtle">
          <span className="inline-flex items-center gap-1">
            <Icon name={CHANNEL_ICON[conversation.channel] ?? "message-square"} className="size-3" />
            {t(`engage.channels.${conversation.channel}`, { defaultValue: conversation.channel })}
          </span>
          {agentName(conversation.agent_id) ? (
            <>
              <span aria-hidden="true">·</span>
              <span className="inline-flex items-center gap-1">
                <Icon name="bot" className="size-3" />
                {agentName(conversation.agent_id)}
              </span>
            </>
          ) : null}
          {startedAt ? (
            <>
              <span aria-hidden="true">·</span>
              <span>{t("engage.inbox.thread.started", { time: fmt.dateTime(startedAt) })}</span>
            </>
          ) : null}
        </div>
      </div>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        <Select className="h-8 w-32 text-xs" aria-label={t("engage.inbox.status.label")} value={status} disabled={!permissions.reply || actions.setStatus.isPending} onChange={(e) => { onStatus(e.target.value); }}>
          {EDITABLE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`engage.inbox.status.${s}`)}
            </option>
          ))}
        </Select>
        <Select className="h-8 w-28 text-xs" aria-label={t("engage.inbox.priority.label")} value={conversation.priority} disabled={!permissions.reply || actions.setPriority.isPending} onChange={(e) => { if (isConversationPriority(e.target.value)) onPriority(e.target.value); }}>
          {CONVERSATION_PRIORITIES.map((priority) => (
            <option key={priority} value={priority}>
              {t(`engage.inbox.priority.${priority}`)}
            </option>
          ))}
        </Select>
        <UnsnoozeButton conversation={conversation} actions={actions} canReply={permissions.reply} now={now} />
        <RunWorkflowMenu conversationId={conversation.id} canReply={permissions.reply} />
        {permissions.assign ? <AssignMenu conversation={conversation} onAssign={(patch) => actions.patch.mutateAsync(patch)} disabled={actions.patch.isPending} /> : null}
        <ConvertButton canConvert={ticketSupport.canConvert && !hasTicket} onClick={() => { setConverting(true); }} />
        {permissions.takeover && conversation.needs_staff && !conversation.ai_paused ? (
          <Button size="sm" variant="secondary" loading={busy} onClick={onHandBack} data-testid="handback">
            <Icon name="bot" />
            {t("engage.inbox.thread.returnToAi")}
          </Button>
        ) : null}
        {permissions.takeover ? (
          <Button size="sm" variant={conversation.ai_paused ? "secondary" : "primary"} loading={busy} onClick={onTakeoverToggle} data-testid="takeover-toggle">
            <Icon name={conversation.ai_paused ? "bot" : "headset"} />
            {conversation.ai_paused ? t("engage.inbox.thread.returnToAi") : t("engage.inbox.thread.takeOver")}
          </Button>
        ) : null}
        <Button variant="ghost" size="sm" iconOnly aria-label={t("engage.inbox.thread.toggleDetails")} onClick={onToggleDetails}>
          <Icon name="panel-left" />
        </Button>
      </div>
      <ConvertTicketDialog detail={detail} actions={actions} open={converting} onOpenChange={setConverting} />
    </div>
  );
}
